import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";

import { useDialogFocus } from "../../app/useDialogFocus";
import { getProjectRepository } from "../../data/projectRepository";
import { demoProjectName } from "../shared/resourceRoomDemoData";
import type { ResourceRoomProjectSummary } from "./resourceRoomTypes";
import "./projects.css";
import "./resource-room-projects.css";

type ProjectAction =
  | { kind: "create" }
  | { kind: "rename"; project: ResourceRoomProjectSummary }
  | { kind: "delete"; project: ResourceRoomProjectSummary }
  | null;

const guideSteps = [
  ["学校基础数据", "导入原班课表，完善学生、节次和课程性质。"],
  ["资源资料", "维护干预课程、教师、教室和三类不可排时间。"],
  ["干预排课", "建立个训或小组计划，一键排课或逐项选择时段。"],
  ["课表与导出", "查看资源教室、教师、学生和班级四类单双周课表。"]
] as const;

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("zh-CN", {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(new Date(value));
}

function GuideDialog({ onClose }: { onClose: () => void }): React.JSX.Element {
  const ref = useDialogFocus(onClose);
  return (
    <div className="modal-backdrop" role="presentation">
      <section
        ref={ref}
        className="modal-card guide-dialog resource-guide-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="resource-guide-title"
      >
        <div className="guide-dialog-heading">
          <h2 id="resource-guide-title">4 步完成资源教室排课</h2>
          <button className="dialog-close" type="button" aria-label="关闭使用说明" onClick={onClose}>
            ×
          </button>
        </div>
        <ol className="guide-steps">
          {guideSteps.map(([title, text], index) => (
            <li key={title}>
              <div className="guide-step-top">
                <span className="guide-step-num">{index + 1}</span>
                <span className="guide-step-icon" aria-hidden="true">{title.slice(0, 1)}</span>
              </div>
              <h3>{title}</h3>
              <p>{text}</p>
            </li>
          ))}
        </ol>
        <p className="guide-tip">
          学生原班课表只能通过 Excel 重新导入更新。项目持续可编辑，数据变化后系统会重新检查已排计划。
        </p>
      </section>
    </div>
  );
}

export function ResourceRoomProjectsPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const [searchDraft, setSearchDraft] = useState("");
  const [query, setQuery] = useState("");
  const [action, setAction] = useState<ProjectAction>(null);
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [guideOpen, setGuideOpen] = useState(false);
  const projects = useQuery({
    queryKey: ["resource-room-projects", query],
    queryFn: async () => (await getProjectRepository()).list(query)
  });
  const mutation = useMutation({
    mutationFn: async () => {
      if (!action) return undefined;
      const repository = await getProjectRepository();
      if (action.kind === "create") return repository.create(name);
      if (action.kind === "rename") {
        return repository.rename(action.project.id, name, action.project.document_revision);
      }
      await repository.delete(action.project.id);
      return undefined;
    },
    onSuccess: async (result) => {
      const created = action?.kind === "create";
      await queryClient.invalidateQueries({ queryKey: ["resource-room-projects"] });
      setAction(null);
      setName("");
      setMessage("");
      if (created && result) window.location.assign(`/projects/${result.id}`);
    },
    onError: (error: unknown) => {
      setMessage(error instanceof Error ? error.message : "操作失败，请稍后重试");
    }
  });
  const dialogRef = useDialogFocus(
    () => setAction(null),
    Boolean(action) && !mutation.isPending,
    Boolean(action)
  );

  function openAction(next: Exclude<ProjectAction, null>): void {
    setAction(next);
    setMessage("");
    setName(next.kind === "rename" ? next.project.name : "");
  }

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    mutation.mutate();
  }

  const items = projects.data?.items ?? [];
  const recent = query
    ? null
    : items.reduce<ResourceRoomProjectSummary | null>(
        (latest, item) => (!latest || item.updated_at > latest.updated_at ? item : latest),
        null
      );

  return (
    <main className="page-shell projects-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">学期项目</p>
          <h1>创建或继续资源教室课表</h1>
          <p className="lede">一个项目对应一个学期，项目之间互不影响。</p>
        </div>
        <button className="guide-button" type="button" onClick={() => setGuideOpen(true)}>
          4 步快速上手
        </button>
      </header>

      {projects.isLoading ? <p className="state-message">正在加载学期项目…</p> : null}
      {projects.isError ? <p className="error-message">项目列表加载失败，请刷新重试。</p> : null}

      {!projects.isLoading && !projects.isError ? (
        <>
          <section className={`continue-section${recent ? "" : " no-recent"}`} aria-label="开始工作">
            {recent ? (
              <div className="continue-card">
                <div className="continue-card-main">
                  <p className="continue-kicker">继续最近项目</p>
                  <h2 className="continue-title">{recent.name}</h2>
                  <p className="continue-meta">更新于 {formatDate(recent.updated_at)}</p>
                </div>
                <a className="solid-button continue-action" href={`/projects/${recent.id}`}>继续</a>
              </div>
            ) : null}
            <div className="new-project-card">
              <p className="new-project-title">开始一个新学期</p>
              <button className="solid-button" type="button" onClick={() => openAction({ kind: "create" })}>
                新建学期项目
              </button>
            </div>
          </section>

          <section className="project-library-section" aria-labelledby="resource-library-title">
            <div className="library-head">
              <div>
                <p className="section-kicker">当前浏览器</p>
                <h2 id="resource-library-title">学期项目库</h2>
              </div>
              <form
                className="search-bar library-search"
                onSubmit={(event) => {
                  event.preventDefault();
                  setQuery(searchDraft.trim());
                }}
              >
                <label htmlFor="resource-project-search">搜索项目</label>
                <div className="search-controls">
                  <input
                    id="resource-project-search"
                    value={searchDraft}
                    onChange={(event) => setSearchDraft(event.target.value)}
                    placeholder="例如：2026 秋季学期"
                  />
                  <button className="secondary-button" type="submit">搜索</button>
                  {query ? (
                    <button className="text-button" type="button" onClick={() => { setSearchDraft(""); setQuery(""); }}>
                      清除
                    </button>
                  ) : null}
                </div>
              </form>
            </div>
            {items.length === 0 ? (
              <div className="empty-card compact">
                <div className="empty-icon" aria-hidden="true">学</div>
                <h2>{query ? "没有匹配的项目" : "还没有学期项目"}</h2>
                <p>{query ? "换个关键词试试。" : "新建项目后，从导入学生原班课表开始。"}</p>
              </div>
            ) : (
              <div className="project-library" role="table" aria-label="学期项目列表">
                <div role="rowgroup">
                  {items.map((project) => (
                    <div className="project-row" role="row" key={project.id}>
                      <div className="project-row-main" role="cell">
                        <a className="project-link" href={`/projects/${project.id}`}>{project.name}</a>
                        <span className="project-row-meta">更新于 {formatDate(project.updated_at)}</span>
                      </div>
                      <div className="project-row-status" role="cell">
                        <span className="pill">本地保存</span>
                      </div>
                      <div className="project-row-actions" role="cell">
                        <a className="mini-button" href={`/projects/${project.id}`}>进入</a>
                        <button className="mini-button" type="button" onClick={() => openAction({ kind: "rename", project })}>改名</button>
                        <button className="mini-button danger-text" type="button" onClick={() => openAction({ kind: "delete", project })}>删除</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        </>
      ) : null}

      {guideOpen ? <GuideDialog onClose={() => setGuideOpen(false)} /> : null}
      {action ? (
        <div className="modal-backdrop" role="presentation">
          <section ref={dialogRef} className="modal-card" role="dialog" aria-modal="true" aria-labelledby="project-action-title">
            <form onSubmit={submit}>
              <h2 id="project-action-title">
                {action.kind === "create" ? "新建学期项目" : action.kind === "rename" ? "修改项目名称" : "删除学期项目"}
              </h2>
              {action.kind === "delete" ? (
                <p>将删除“{action.project.name}”及其中全部学生、计划和课表。此操作无法撤销。</p>
              ) : (
                <label className="field">
                  <span>项目名称</span>
                  <input required autoComplete="off" value={name} onChange={(event) => setName(event.target.value)} />
                  {action.kind === "create" ? (
                    <button className="text-button demo-fill-button" type="button" onClick={() => setName(demoProjectName())}>
                      填入模拟项目名称
                    </button>
                  ) : null}
                </label>
              )}
              {message ? <p className="error-message" role="alert">{message}</p> : null}
              <div className="modal-actions">
                <button className="secondary-button" type="button" onClick={() => setAction(null)}>取消</button>
                <button className={`primary-button${action.kind === "delete" ? " danger-button" : ""}`} type="submit" disabled={mutation.isPending}>
                  {mutation.isPending ? "正在处理…" : action.kind === "delete" ? "确认删除" : "确认"}
                </button>
              </div>
            </form>
          </section>
        </div>
      ) : null}
    </main>
  );
}
